<template>
  <div class="bookings-planning-table">
    <!-- SEARCH -->
    <b-row v-if="showSearch" class="mb-2 d-flex flex-wrap justify-content-end">
      <b-col cols="9" md="auto" class="mb-1 mb-md-0 justify-content-end">
        <b-form-input id="filterInput" ref="filterInput" v-model="filter" type="search"
          :placeholder="`${$t('Cerca')}...`" :disabled="isBusy" debounce="400" autofocus />
      </b-col>
    </b-row>

    <!-- BOOKINGS TABLE -->
    <b-row>
      <b-col cols="12">
        <b-table striped borderless responsive class="position-relative" :busy.sync="isBusy" :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc" sort-icon-left :items="bookings" show-empty
          :empty-text="$t('errors.emptyTable.title')" :empty-filtered-text="$t('errors.emptyTable.title')"
          :fields="fields" :filter="filter">
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle mr-2" />
              <strong>{{ $t("Carregant") }}...</strong>
            </div>
          </template>

          <!-- Column: localizator -->
          <template #cell(localizator)="data">
            <span class="text-nowrap font-weight-bold">
              {{ data.value }}
            </span>
          </template>

          <!-- Column: accommodation -->
          <template #cell(accommodation)="data">
            <router-link
              v-if="data.item.accommodationSlug"
              :to="{ name: 'foravila-accommodation-view', params: { slug: data.item.accommodationSlug } }"
            >{{ data.value }}</router-link>
            <span v-else>{{ data.value }}</span>
          </template>

          <!-- Column: checkin -->
          <template #cell(checkin)="data">
            {{ formatBookingDate(data.item.checkin) }}
            <feather-icon icon="ArrowRightIcon" size="10" class="mx-25" />
            {{ formatBookingDate(data.item.checkout) }}
          </template>

          <!-- Column: requests -->
          <template #cell(requests)="data">
            <div class="d-flex">
              <b-badge pill :variant="data.item.babyCots ? 'primary' : 'secondary'" class="mr-50">
                🚼 <span>{{ data.item.babyCotsText }}</span>
              </b-badge>
              <b-badge pill :variant="data.item.babyChairs ? 'primary' : 'secondary'">
                🪑 <span>{{ data.item.babyChairsText }}</span>
              </b-badge>
            </div>
          </template>

          <!-- Column: bedLinenChange -->
          <template #cell(bedLinenChange)="data">
            <span v-if="!data.value || !data.value.length" class="text-muted">{{ $t("No") }}</span>
            <div v-else>
              <b-badge v-for="(date, i) in data.value" :key="i" class="text-nowrap mr-25">
                {{ formatBookingDate(date) }}
              </b-badge>
            </div>
          </template>

          <!-- Column: actions -->
          <template #cell(actions)="data">
            <b-button
              size="sm"
              :variant="data.item.cleaningCompleted ? 'success' : 'outline-secondary'"
              @click="$emit('toggle-cleaning', data.item)"
            >
              <feather-icon :icon="data.item.cleaningCompleted ? 'CheckCircleIcon' : 'CircleIcon'" size="14" class="mr-25" />
              {{ data.item.cleaningCompleted ? $t('Netejat') : $t('Pendent') }}
            </b-button>
          </template>
        </b-table>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BTable, BRow, BCol, BFormInput, BSpinner, BBadge, BButton,
} from "bootstrap-vue";
import FeatherIcon from "@core/components/feather-icon/FeatherIcon.vue";
import { formatDateObjectToDate } from "@formatters";

export default {
  components: {
    BTable, BRow, BCol, BFormInput, BSpinner, BBadge, BButton, FeatherIcon,
  },
  props: {
    showSearch: {
      type: Boolean,
      default: () => false,
    },
    loading: {
      type: Boolean,
      default: () => false,
    },
    bookings: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isBusy: this.loading,
      sortBy: "checkin",
      sortDesc: false,
      filter: null,
      fields: [],
    };
  },
  watch: {
    loading(loading) {
      this.isBusy = loading;
    },
  },
  created() {
    this.initFields();
  },
  methods: {
    initFields() {
      this.fields.push({ key: "localizator", label: this.$t("Reserva"), thStyle: { maxWidth: "180px" } });
      this.fields.push({ key: "checkin", label: this.$t("Estada"), sortable: true, thStyle: { width: "225px" } });
      this.fields.push({ key: "accommodation", label: this.$t("Allotjament") });
      this.fields.push({ key: "guests", label: this.$t("Hostes") });
      this.fields.push({ key: "checkinTime", label: this.$t("Hora d'entrada"), sortable: true });
      this.fields.push({ key: "requests", label: this.$t("Peticions") });
      this.fields.push({ key: "bedLinenChange", label: this.$t("Bed linen change") });
      this.fields.push({ key: "actions", label: this.$t("Neteja"), thStyle: { width: "150px" } });
    },
    formatBookingDate(date) {
      return formatDateObjectToDate(date, this.$i18n.locale);
    }
  },
};
</script>
